import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton";
import { FormattedMessage, useIntl } from "react-intl";
import clsx from "clsx";
import { getReaderNavLink } from "../../../bookshelfLinks";
import useAssetActivate from "../../actions/explore/activate/useAssetActivate";
import useExploreFetcher from "../../actions/explore/useExploreFetcher";
import LoaderButton from "../../../../common/core-ui/LoaderButton";
import {
  ExploreAssetAction,
  useExploreAssetActionType,
} from "./useExploreAssetActionType";
import { ReactNode, useMemo, useState } from "react";
import AssetPanel from "../asset-panel/AssetPanel.tsx";

type Labels = Record<
  ExploreAssetAction,
  { label: ReactNode; ariaLabel: string }
>;

interface BorrowOrOpenBookButtonProps {
  asset: VST.Bridge.Asset;
  className?: string;
  returnDetail?: string;
  enableShowDetails?: boolean;
  alternateLabels?: Partial<Labels>;
}

function useDefaultLabels(asset: VST.Bridge.Asset): Labels {
  const intl = useIntl();

  return useMemo(
    () => ({
      [ExploreAssetAction.None]: {
        label: <FormattedMessage defaultMessage="Book Details" />,
        ariaLabel: intl.formatMessage(
          { defaultMessage: "Book Details for {title}" },
          { title: asset.title }
        ),
      },
      [ExploreAssetAction.Open]: {
        label: <FormattedMessage defaultMessage="Open Book" />,
        ariaLabel: intl.formatMessage(
          { defaultMessage: "Open Book {title}" },
          { title: asset.title }
        ),
      },
      [ExploreAssetAction.Borrow]: {
        label: <FormattedMessage defaultMessage="Borrow" />,
        ariaLabel: intl.formatMessage(
          { defaultMessage: "Borrow {title}" },
          { title: asset.title }
        ),
      },
    }),
    [intl, asset]
  );
}

export default function BorrowOrOpenBookButton({
  asset,
  className,
  returnDetail,
  enableShowDetails,
  alternateLabels,
}: BorrowOrOpenBookButtonProps) {
  const activateAsset = useAssetActivate(asset);
  const { activeFetcher, activationComplete } = useExploreFetcher(asset);
  const actionType = useExploreAssetActionType(asset);
  const [detailsOpenTime, setDetailsOpenTime] = useState(0);
  const defaultLabels = useDefaultLabels(asset);

  const labels = alternateLabels?.[actionType] ?? defaultLabels[actionType];

  if (actionType === ExploreAssetAction.None) {
    if (enableShowDetails) {
      return (
        <>
          <BaseLinkOrButton
            aria-label={labels.ariaLabel}
            {...getReaderNavLink(asset.vbid, {
              needsActivation: !asset.active,
              returnDetail,
            })}
            className={clsx("vst-button-pill", className)}
          >
            {labels.label}
          </BaseLinkOrButton>
          {detailsOpenTime > 0 && (
            <AssetPanel
              key={detailsOpenTime}
              onRequestClose={() => setDetailsOpenTime(0)}
              explore={asset}
            />
          )}
        </>
      );
    }
    return null;
  }

  if (actionType === ExploreAssetAction.Open) {
    return (
      <BaseLinkOrButton
        aria-label={labels.ariaLabel}
        {...getReaderNavLink(asset.vbid, {
          needsActivation: !asset.active,
          returnDetail,
        })}
        className={clsx("vst-button-pill", className)}
      >
        {labels.label}
      </BaseLinkOrButton>
    );
  }

  if (actionType === ExploreAssetAction.Borrow) {
    return (
      <LoaderButton
        onClick={activateAsset}
        aria-label={labels.ariaLabel}
        className={clsx("vst-button-pill", className)}
        // There is a very quick delay between the action completing and the asset data updating, so
        // keep the button in loading state once the action completes until the data is updated.
        loading={activeFetcher?.state === "submitting" || !!activationComplete}
      >
        {labels.label}
      </LoaderButton>
    );
  }
}
